import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {getExpLvls, getSkills, getSkillTypes} from "../../service/dataService";
import {getUserSkills} from "../../service/userService";
import BasicGrid from "../grid/BasicGrid";
import EditSkillModal from "../modals/skills/EditSkillModal";
import {Button, ButtonToolbar, Divider, Form, IconButton, Stack, toaster, Tooltip, Whisper} from "rsuite";
import SuccessNotification from "../notifications/SuccessNotification";
import {SKILL_ADD_ERROR, SKILL_ADD_SUCCESS, SKILL_EDIT_ERROR, SKILL_EDIT_SUCCESS} from "../../service/constants";
import ErrorNotification from "../notifications/ErrorNotification";
import AddSkillModal from "../modals/skills/AddSkillModal";
import SkillViewerModal from "../modals/skills/SkillViewerModal";
import {InfoRound} from "@rsuite/icons";

const defaultColumns = [
    {
        key: 'skillName',
        label: 'Skill Name',
    },
    {
        key: 'skillType',
        label: 'Skill Type',
    },
    {
        key: 'skillExpLevel',
        label: 'Experience Level',
    },
    {
        key: 'yearsExp',
        label: 'Years Experience',
    },
];

const Skills = () => {

    // state
    const userInfo = useSelector((state) => state.user.userInfo );
    const [unformattedSkills, setUnformattedSkills] = useState([]);
    const [skills, setSkills] = useState([]);
    const [skillTypes, setSkillTypes] = useState([]);
    const [expLvls, setExpLvls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [userSkills, setUserSkills] = useState([]);

    // add modal
    const [addModalOpen, setAddModalOpen] = useState(false);

    // edit modal
    const [selectedSkill, setSelectedSkill] = useState({});
    const [editModalOpen, setEditModalOpen] = useState(false);

    // skill viewer modal
    const [viewerModalOpen, setViewerModalOpen] = useState(false);

    const handleAddClose = useCallback(({result, closeModal}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={SKILL_ADD_SUCCESS} />, { placement: 'topCenter' });
            setLoading(true);
            getUserSkills(userInfo.id).then((skills) => {
                setUserSkills(skills);
                setLoading(false);
            });
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={SKILL_ADD_ERROR} />, { placement: 'topCenter' });
        }
        if (closeModal) {
            setAddModalOpen(false);
        }
    },[userInfo.id]);

    const handleEditClose = useCallback(({result}) => {
        if(result === 'success') {
            toaster.push(<SuccessNotification message={SKILL_EDIT_SUCCESS} />, { placement: 'topCenter' });
            setLoading(true);
            getUserSkills(userInfo.id).then((skills) => {
                setUserSkills(skills);
                setLoading(false);
            });
        } else if (result === 'error') {
            toaster.push(<ErrorNotification message={SKILL_EDIT_ERROR} />, { placement: 'topCenter' });
        }
        setEditModalOpen(false);
    }, [userInfo.id]);

    const handleViewerClose = useCallback(() => {
        setViewerModalOpen(false);
    }, []);

    // table
    const [columnKeys] = useState(defaultColumns.map(column => column.key));
    const columns = defaultColumns.filter(column => columnKeys.some(key => key === column.key));

    const setClickedSkill = useCallback((rowData) => {
        setEditModalOpen(true);
        setSelectedSkill(rowData);
    },[]);

    useEffect(() => {
        setLoading(true);
        getSkills().then((data) => {
                // format the data into InputPicker format for now
                setSkills(data.map(ele => ({label: ele.skillName, value: ele.skillName})));
                setUnformattedSkills(data);
            }
        );
        getSkillTypes().then((data) => {
                // format the data into InputPicker format for now
                setSkillTypes(data.map(ele => ({label: ele.skillTypeName, value: ele.skillTypeName})));
            }
        );
        getExpLvls().then((data) => {
                // format the data into InputPicker format for now
                setExpLvls(data.map(ele => ({label: ele.expLevelName, value: ele.expLevelName})));
            }
        );
        getUserSkills(userInfo.id).then((skills) => {
            setUserSkills(skills);
            setLoading(false);
        });
    }, [userInfo.id]);

    return useMemo(() => {
        return (
        <div className="main-content">
            <h2>Skills</h2>
            <Stack spacing={2} direction='column' alignItems='flex-start' style={{paddingBottom: '0.5rem'}}>
                <p>
                    Click a row to edit/delete.
                </p>
                <p>
                    Only Experience Level and years is modifiable. Add/delete skills as needed otherwise.
                </p>
            </Stack>
            <Form.Group>
                <ButtonToolbar>
                    <Button appearance="primary" onClick={() => setAddModalOpen(true)}>Add Skill</Button>
                    <Button appearance="primary" onClick={() => setViewerModalOpen(true)}>Skill Viewer</Button>
                    <Whisper speaker={<Tooltip> The Skill Viewer shows all Skills currently tracked by this tool</Tooltip>}>
                        <span>
                            <IconButton appearance="ghost" disabled style={{pointerEvents: 'none' }} icon={<InfoRound />} />
                        </span>
                    </Whisper>
                </ButtonToolbar>
            </Form.Group>
            <Divider />
            <BasicGrid
                loading={loading}
                columns={columns}
                data={userSkills}
                handleClick={setClickedSkill}
                exportTitle={`${userInfo.lastName}-${userInfo.firstName}-skills`}
            />
            <AddSkillModal
                open={addModalOpen}
                handleClose={handleAddClose}
                skills={skills}
                skillTypes={skillTypes}
                skillExpLevels={expLvls}
                userId={userInfo.id}
                unformattedSkills={unformattedSkills}
            />
            <EditSkillModal
                open={editModalOpen}
                handleClose={handleEditClose}
                skill={selectedSkill}
                skillExpLevels={expLvls}
            />
            <SkillViewerModal
                open={viewerModalOpen}
                handleClose={handleViewerClose}
                unformattedSkills={unformattedSkills}
            />
        </div>
        );
    }, [
        addModalOpen,
        columns,
        editModalOpen,
        expLvls,
        handleAddClose,
        handleEditClose,
        handleViewerClose,
        loading,
        selectedSkill,
        setClickedSkill,
        skills,
        skillTypes,
        unformattedSkills,
        userInfo.id,
        userInfo.firstName,
        userInfo.lastName,
        userSkills,
        viewerModalOpen,
    ]);
}

export default Skills;